import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Divider, IconButton, Tooltip, Chip,
    Avatar, Skeleton, useTheme, useMediaQuery,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    ShoppingBag as ItemsIcon,
    ReceiptLong as ReceiptIcon,
    ContentCopy as CopyIcon,
    LocalShipping as ShippingIcon,
    CalendarToday as CalendarIcon,
    Inventory2 as PackageIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../services/api';

const GOLD = '#bc965e';
const API_BASE_URL = process.env.REACT_APP_API_URL.replace('/api', '');

const fmt = (value) => parseFloat(value || 0).toFixed(2);

const getPhotoUrl = (path) => {
    if (!path) return undefined;
    const normalized = path.replace(/\\/g, '/');
    return `${API_BASE_URL}${normalized.startsWith('/') ? '' : '/'}${normalized}`;
};

const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
        .then(() => toast.success(`${label} copied`))
        .catch(() => toast.error('Could not copy'));
};

// ── Payment status → color mapping (single source of truth) ──
const PAYMENT_STATUS_STYLES = {
    paid: { label: 'Paid', color: '#4caf50', bg: 'rgba(76,175,80,0.12)', border: 'rgba(76,175,80,0.35)' },
    failed: { label: 'Failed', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.35)' },
    default: { label: 'Pending', color: '#ff9800', bg: 'rgba(255,152,0,0.12)', border: 'rgba(255,152,0,0.35)' },
};
const getPaymentStatusStyle = (status) => PAYMENT_STATUS_STYLES[status] || PAYMENT_STATUS_STYLES.default;

const Section = ({ title, icon, action, children, sx = {} }) => (
    <Box
        className="glass-content-container"
        sx={{
            borderRadius: 2,
            border: '1px solid rgba(188,150,94,0.16)',
            p: { xs: 1.5, sm: 2 },
            ...sx,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {icon}
                <Typography sx={{
                    color: GOLD,
                    fontWeight: 800,
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}>
                    {title}
                </Typography>
            </Box>
            {action}
        </Box>
        {children}
    </Box>
);

const OrderItemRow = ({ item, isLast }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            p: { xs: 1.25, sm: 1.5 },
            borderBottom: isLast ? 'none' : '1px solid rgba(188,150,94,0.1)',
            transition: 'background 0.15s ease',
            '&:hover': { background: 'rgba(188,150,94,0.04)' },
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Avatar
                variant="rounded"
                src={getPhotoUrl(item.Photo) }
                sx={{
                    width: 40, height: 40, flexShrink: 0,
                    bgcolor: 'rgba(188,150,94,0.1)',
                    border: '1px solid rgba(188,150,94,0.18)',
                }}
            >
                <PackageIcon sx={{ fontSize: 18, color: GOLD }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: 'white', fontWeight: 650, fontSize: '0.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {item.ProductName}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.74rem', mt: 0.25 }}>
                    {item.Size ? `Size: ${item.Size} | ` : ''}{item.Quantity} x Rs {fmt(item.UnitPrice)}
                </Typography>
            </Box>
        </Box>
        <Typography sx={{ color: GOLD, fontWeight: 800, fontSize: '0.86rem', fontFamily: 'monospace', flexShrink: 0 }}>
            Rs {fmt(item.LineTotal)}
        </Typography>
    </Box>
);

const CopyableField = ({ label, value, color = GOLD, bg, border }) => (
    <Box sx={{
        mt: 1, p: 1.25, borderRadius: 1.5,
        background: bg || 'rgba(188,150,94,0.05)',
        border: `1px solid ${border || 'rgba(188,150,94,0.2)'}`,
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ color, fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {label}
            </Typography>
            <Tooltip title={`Copy ${label}`}>
                <IconButton size="small" onClick={() => copyToClipboard(value, label)} sx={{ color: 'rgba(255,255,255,0.4)', p: 0.3 }}>
                    <CopyIcon sx={{ fontSize: 13 }} />
                </IconButton>
            </Tooltip>
        </Box>
        <Typography sx={{ color: 'white', fontFamily: 'monospace', fontSize: '0.78rem', mt: 0.25, wordBreak: 'break-all' }}>
            {value}
        </Typography>
    </Box>
);

const DetailSkeleton = () => (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: 'rgba(188,150,94,0.12)' }} />
            <Box>
                <Skeleton variant="text" width={140} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                <Skeleton variant="text" width={100} height={18} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
            </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 340px' }, gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[88, 70, 180].map((h, i) => (
                    <Skeleton key={i} variant="rounded" height={h} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
                ))}
            </Box>
            <Skeleton variant="rounded" height={260} sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
        </Box>
    </Box>
);

const AdminOrderDetail = () => {
    const { billId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);

        API.get(`/admin/orders/${billId}`)
            .then(({ data }) => {
                if (!mounted) return;
                if (data.success) setOrder(data.data);
                else toast.error(data.message || 'Order not found');
            })
            .catch(() => toast.error('Failed to load order details'))
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => { mounted = false; };
    }, [billId]);

    if (loading) return <DetailSkeleton />;

    if (!order) {
        return (
            <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                <Button onClick={() => navigate('/admin/orders')} startIcon={<BackIcon />} sx={{ color: GOLD, textTransform: 'none', mb: 2 }}>
                    Back to Orders
                </Button>
                <Box className="glass-content-container" sx={{ textAlign: 'center', py: 6 }}>
                    <ReceiptIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.2)', mb: 1 }} />
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>Order not found</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', mt: 0.5 }}>
                        Check the bill id and try again.
                    </Typography>
                </Box>
            </Box>
        );
    }

    const couponDiscount = parseFloat(order.CouponDiscount || 0);
    const calculatedDiscount = parseFloat(order.SubTotal || 0) - parseFloat(order.TotalAmount || 0);
    const displayDiscount = couponDiscount > 0 ? couponDiscount : (order.UsedCouponCode && calculatedDiscount > 0 ? calculatedDiscount : 0);
    const itemCount = order.items?.length || 0;
    const createdAt = new Date(order.CreatedAt);
    const paymentStyle = getPaymentStatusStyle(order.payment_status);

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1.5,
                mb: 2.5,
                flexDirection: { xs: 'column', sm: 'row' },
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <IconButton
                        onClick={() => navigate('/admin/orders')}
                        size="small"
                        sx={{ color: GOLD, border: '1px solid rgba(188,150,94,0.35)', borderRadius: 1.5, flexShrink: 0 }}
                    >
                        <BackIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{
                                color: 'white', fontWeight: 800, fontSize: { xs: '1rem', md: '1.35rem' },
                                fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                #{order.BillId}
                            </Typography>
                            <Tooltip title="Copy bill id">
                                <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(order.BillId, 'Bill id')}
                                    sx={{ color: 'rgba(255,255,255,0.35)', p: 0.4, '@media print': { display: 'none' } }}
                                >
                                    <CopyIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                            <CalendarIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.32)' }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.78rem' }}>
                                {createdAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '1fr 340px' },
                gap: 2,
                alignItems: 'start',
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Section title="Customer" icon={<ReceiptIcon sx={{ color: GOLD, fontSize: 17 }} />}>
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.92rem', mb: 0.75, pl: 2, textAlign: 'left', textTransform: 'capitalize' }}>
                            {order.CustomerName}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 2.5 } }}>
                            {order.PhoneNumber && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PhoneIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)' }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.82rem' }}>{order.PhoneNumber}</Typography>
                                </Box>
                            )}
                            {order.EmailID && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                    <EmailIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', flexShrink: 0 }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: '0.82rem', wordBreak: 'break-all' }}>{order.EmailID}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Section>

                    <Section title="Delivery Address" icon={<LocationIcon sx={{ color: GOLD, fontSize: 17 }} />}>
                        {order.Address ? (
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.84rem', lineHeight: 1.7, textAlign: 'left', textTransform: 'capitalize' }}>
                                {[order.FullName, order.Address, order.City, order.State, order.Pincode].filter(Boolean).join(', ')}
                            </Typography>
                        ) : (
                            <Typography sx={{ color: 'rgba(255,255,255,0.42)', fontSize: '0.84rem' }}>No delivery address saved.</Typography>
                        )}
                    </Section>

                    <Section title={`Items (${itemCount})`} icon={<ItemsIcon sx={{ color: GOLD, fontSize: 17 }} />}>
                        <Box sx={{ border: '1px solid rgba(188,150,94,0.16)', borderRadius: 1, overflow: 'hidden' }}>
                            {itemCount === 0 ? (
                                <Box sx={{ p: 2.5, textAlign: 'center' }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>No items recorded for this order.</Typography>
                                </Box>
                            ) : (
                                order.items.map((item, index) => (
                                    <OrderItemRow
                                        key={item.BillItemId || index}
                                        item={item}
                                        isLast={index === order.items.length - 1}
                                    />
                                ))
                            )}
                        </Box>
                    </Section>
                </Box>

                <Box sx={{
                    position: { lg: isDesktop ? 'sticky' : 'static' },
                    top: { lg: 16 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <Section title="Payment Summary" icon={<ReceiptIcon sx={{ color: GOLD, fontSize: 17 }} />}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.84rem' }}>Subtotal</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.84rem', fontFamily: 'monospace' }}>Rs {fmt(order.SubTotal)}</Typography>
                        </Box>

                        {displayDiscount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography sx={{ color: '#4caf50', fontSize: '0.84rem' }}>Coupon ({order.UsedCouponCode})</Typography>
                                <Typography sx={{ color: '#4caf50', fontSize: '0.84rem', fontWeight: 700, fontFamily: 'monospace' }}>-Rs {fmt(displayDiscount)}</Typography>
                            </Box>
                        )}

                        <Divider sx={{ borderColor: 'rgba(188,150,94,0.16)', my: 1.25 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.92rem' }}>Total</Typography>
                            <Typography sx={{ color: GOLD, fontWeight: 900, fontSize: '1.05rem', fontFamily: 'monospace' }}>Rs {fmt(order.TotalAmount)}</Typography>
                        </Box>

                        <Divider sx={{ borderColor: 'rgba(188,150,94,0.16)', my: 1.25 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem' }}>Payment Method</Typography>
                            <Typography sx={{ color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>
                                {order.payment_method === 'cod' ? '💵 Cash on Delivery' : '💳 Online Payment'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: order.razorpay_payment_id ? 1 : 0 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem' }}>Payment Status</Typography>
                            <Chip
                                label={paymentStyle.label}
                                size="small"
                                sx={{
                                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                                    backgroundColor: paymentStyle.bg,
                                    border: `1px solid ${paymentStyle.border}`,
                                    color: paymentStyle.color,
                                }}
                            />
                        </Box>

                        {order.razorpay_payment_id && (
                            <CopyableField label="Payment ID" value={order.razorpay_payment_id} />
                        )}
                    </Section>

                    {(order.AWBCode || order.ShiprocketOrderId) && (
                        <Section title="Shipping" icon={<ShippingIcon sx={{ color: GOLD, fontSize: 17 }} />}>
                            {order.AWBCode && (
                                <CopyableField
                                    label="AWB Code"
                                    value={order.AWBCode}
                                    color="#8b5cf6"
                                    bg="rgba(139,92,246,0.06)"
                                    border="rgba(139,92,246,0.2)"
                                />
                            )}
                            {order.ShiprocketOrderId && (
                                <Chip
                                    label={`Shiprocket | ${order.ShiprocketOrderId}`}
                                    size="small"
                                    sx={{ mt: order.AWBCode ? 1.25 : 0, maxWidth: '100%', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(188,150,94,0.25)' }}
                                    variant="outlined"
                                />
                            )}
                        </Section>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default AdminOrderDetail;